/**
 * Support services and reference reading for the "Caring for you" page.
 *
 * Hardcoded in the bundle on purpose: no fetch, no runtime config. A phone number
 * matters most when the network is off, so the page has to work offline.
 *
 * Two rules govern this module:
 *
 * 1. **Every description here is the service's own wording**, taken from the page
 *    named in `url`. Do not warm it up, shorten it into something friendlier, or
 *    substitute our own summary — a caller needs to recognise the service they are
 *    about to reach. Because these are quoted third-party strings, this module is
 *    deliberately kept separate from the page's own copy module and is exempt from
 *    the copy lint that scans that one.
 * 2. **`last_verified` means someone opened the site and checked the number, the
 *    hours and the region.** `supportResources.test.ts` fails the build when any
 *    service entry passes 90 days, because a dead number at the moment of need is
 *    worse than no number. The reference links carry the same field but sit
 *    outside that test: a guide going quiet is an inconvenience, a helpline going
 *    dead is a harm.
 *
 * UK and Irish entries below verified 2026-07-29; the Brazilian entry 2026-10-10; the
 * Spanish-speaking countries' entries (AR, CL, CO, ES, MX) 2026-10-11.
 */

import { deviceCountry } from '../lib/region';

/**
 * The countries a Spanish reader may be in, by ISO 3166 code — each one a region
 * of its own, because crisis lines are national. Entries for them come with the
 * Spanish translation; a country with none shows no support sections.
 */
export const SPANISH_SPEAKING_COUNTRIES = [
  'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'ES', 'GQ', 'GT',
  'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'US', 'UY', 'VE',
] as const;
export type SpanishSpeakingCountry = (typeof SPANISH_SPEAKING_COUNTRIES)[number];

/** Coarse jurisdiction. UK 0800 numbers do not connect from the Republic of
 *  Ireland, so this is load-bearing, not decoration. */
export type SupportRegion = 'UK' | 'IE' | 'UK_AND_IE' | 'BR' | SpanishSpeakingCountry;

/** The regions whose entries a reader in `country` (an ISO 3166 code) is shown. */
function regionsForCountry(country: string | null): SupportRegion[] {
  if (country === 'GB') return ['UK', 'UK_AND_IE'];
  if (country === 'IE') return ['IE', 'UK_AND_IE'];
  if (country === 'BR') return ['BR'];
  return (SPANISH_SPEAKING_COUNTRIES as readonly string[]).includes(country ?? '')
    ? [country as SpanishSpeakingCountry]
    : [];
}

/**
 * The regions whose entries a reader is shown. There is no region preference, so
 * it is taken from the language: Brazilian Portuguese reads Brazil's services,
 * English reads the UK and Ireland's, as it always has. Spanish is read in some
 * twenty countries, so a Spanish reader gets the services of the country the device
 * is in (lib/region.ts: "es-MX" → MX), taken from the device in hand as DS-5 takes
 * the timezone. A section with no entries for the reader's regions is not shown
 * (CaringForYouPage).
 */
export function supportRegionsFor(
  lang: string,
  country: string | null = lang === 'es' ? deviceCountry(lang) : null,
): SupportRegion[] {
  if (lang === 'pt-BR') return ['BR'];
  if (lang === 'es') return regionsForCountry(country);
  return ['UK', 'IE', 'UK_AND_IE'];
}

/** The entries of `list` for a reader in `lang` (in Spanish, in `country`). */
export function forReaderIn<T extends { region: SupportRegion }>(
  list: T[],
  lang: string,
  country?: string | null,
): T[] {
  const regions = supportRegionsFor(lang, country);
  return list.filter((entry) => regions.includes(entry.region));
}

export type SupportChannelKind = 'phone' | 'text' | 'webchat' | 'email';

export interface SupportChannel {
  kind: SupportChannelKind;
  /** Exactly what the user reads, e.g. "116 123" or "Text SHOUT to 85258". */
  label: string;
  /** `tel:` for phones, `https:` for webchat/email pages. Absent for SMS
   *  shortcodes — an `sms:` link to a shortcode is unreliable across networks. */
  href?: string;
  /** Availability is per CHANNEL, not per service: Blue Cross is reachable from
   *  both jurisdictions, but only its webchat and email are — its 0800 line is UK-only. */
  availableIn: SupportRegion;
}

export interface SupportService {
  id: string;
  name: string;
  /** Coarse enum for the service as a whole. Per-channel nuance lives on the
   *  channel; anything that still needs saying goes in `regionNote`. */
  region: SupportRegion;
  regionNote?: string;
  channels: SupportChannel[];
  hours: string;
  /** The service's own words. See rule 1 above. */
  description: string;
  url: string;
  /** ISO date. Freshness-tested — see rule 2 above. */
  last_verified: string;
}

export interface ReferenceLink {
  id: string;
  name: string;
  /** Who it is shown to (supportRegionsFor). The English-language guides are
   *  listed for the UK and Ireland's readers, where they have always appeared. */
  region: SupportRegion;
  description: string;
  url: string;
  /** Confirmed live, but exempt from the 90-day test — reference reading, not a helpline. */
  last_verified: string;
}

/**
 * Samaritans first, deliberately. It is the only service on this page that behaves
 * identically for every user the app has: free from any phone in both jurisdictions,
 * 24/7. Everything else here is one-jurisdiction, one-channel, or office hours.
 */
export const GENERAL_SUPPORT_SERVICES: SupportService[] = [
  {
    id: 'samaritans',
    name: 'Samaritans',
    region: 'UK_AND_IE',
    channels: [
      { kind: 'phone', label: '116 123', href: 'tel:116123', availableIn: 'UK_AND_IE' },
    ],
    hours: '24 hours a day, every day. Free from any phone, including pay-as-you-go',
    description: "Whatever you’re going through, call us free any time, from any phone.",
    url: 'https://www.samaritans.org/how-we-can-help/contact-samaritan/talk-us-phone/',
    last_verified: '2026-07-29',
  },
  {
    id: 'text_about_it',
    name: 'Text About It',
    region: 'IE',
    channels: [
      { kind: 'text', label: 'Text HELLO to 50808', availableIn: 'IE' },
      {
        kind: 'text',
        label: 'On 48, An Post and Clear Mobile, text 086 1800 280 instead',
        availableIn: 'IE',
      },
    ],
    hours: '24 hours a day, every day. Free',
    description:
      'Text About It is a free, anonymous, 24/7 messaging service providing everything from a calming chat to immediate support.',
    url: 'https://www.textaboutit.ie/',
    last_verified: '2026-07-29',
  },
  {
    id: 'shout',
    name: 'Shout',
    region: 'UK',
    channels: [{ kind: 'text', label: 'Text SHOUT to 85258', availableIn: 'UK' }],
    hours: '24 hours a day, every day. Free',
    description:
      "Shout is the UK’s first and only 24/7 text service for anyone struggling with their mental health.",
    url: 'https://giveusashout.org/',
    last_verified: '2026-07-29',
  },
  {
    id: 'pieta',
    name: 'Pieta',
    region: 'IE',
    channels: [
      { kind: 'phone', label: 'Freephone 1800 247 247', href: 'tel:1800247247', availableIn: 'IE' },
      { kind: 'text', label: 'Or text HELP to 51444 (standard text rates)', availableIn: 'IE' },
    ],
    hours: '24 hours a day, 365 days a year',
    // Pieta's own page leads with suicide and self-harm. Stuart chose this shorter
    // sentence — also Pieta's own wording, from the same page — on 2026-07-29, so the
    // entry stays accurate about what the service is without the app raising suicide
    // with every owner who opens the page.
    description:
      'Crisis intervention support, operated by qualified and experienced therapists, by phone and text.',
    url: 'https://www.pieta.ie/how-we-can-help/helpline/',
    last_verified: '2026-07-29',
  },
  {
    id: 'cvv',
    name: 'CVV – Centro de Valorização da Vida',
    region: 'BR',
    channels: [
      { kind: 'phone', label: '188', href: 'tel:188', availableIn: 'BR' },
      { kind: 'webchat', label: 'Chat', href: 'https://cvv.org.br/chat/', availableIn: 'BR' },
      { kind: 'email', label: 'E-mail', href: 'https://cvv.org.br/e-mail/', availableIn: 'BR' },
    ],
    hours:
      'Telefone 188: 24 horas por dia, sem custo de ligação. Chat: domingos das 15h às 00h, segundas a sextas-feiras das 08h às 00h, sábados das 13h às 00h. E-mail: o atendimento não é imediato',
    // CVV's fuller self-descriptions lead on suicide prevention. Stuart approved this
    // shorter sentence, CVV's own wording from its home page, on 2026-10-10, in the
    // spirit of the Pieta decision.
    description:
      'Converse gratuitamente com um dos nossos voluntários, em todo o Brasil, 24 horas por dia',
    url: 'https://cvv.org.br/',
    last_verified: '2026-10-10',
  },
  {
    id: 'ar_linea_salud_mental_0800',
    name: 'Línea Nacional de Orientación y Apoyo en la Urgencia de Salud Mental',
    region: 'AR',
    channels: [
      { kind: 'phone', label: '0800-999-0091', href: 'tel:08009990091', availableIn: 'AR' },
    ],
    hours: 'Las 24 horas, los 365 días del año. Atención gratuita y confidencial',
    description:
      'El 0800-999-0091 brinda atención gratuita y confidencial ante situaciones de urgencia de salud mental en todo el país.',
    url: 'https://www.argentina.gob.ar/salud/hospitalbonaparte/hospital-en-red/dispositivo-0800',
    last_verified: '2026-10-11',
  },
  {
    id: 'centro_asistencia_suicida',
    name: 'Centro de Asistencia al Suicida',
    region: 'AR',
    regionNote:
      '135: línea gratuita desde Capital y Gran Buenos Aires. 0800 345 1435 y (011) 5275-1135: desde todo el país',
    channels: [
      {
        kind: 'phone',
        label: '135 (línea gratuita desde Capital y Gran Buenos Aires)',
        href: 'tel:135',
        availableIn: 'AR',
      },
      {
        kind: 'phone',
        label: '0800 345 1435 (desde todo el país)',
        href: 'tel:08003451435',
        availableIn: 'AR',
      },
      { kind: 'phone', label: '(011) 5275-1135', href: 'tel:01152751135', availableIn: 'AR' },
    ],
    hours:
      'De 8:00 a 0:00. Los llamados llevan varios minutos y puede ser que las líneas den ocupado',
    // The service's own self-description, which raises suicide or crisis directly.
    // Stuart chose to keep the entry with this wording on 2026-10-11.
    description:
      'Si Usted, o algún familiar o allegado suyo, está atravesando una crisis emocional de cualquier tipo, siente que nada tiene sentido o se encuentra atrapado en una situación a la que no le encuentra salida, no lo dude: Llámenos',
    url: 'https://www.asistenciaalsuicida.org.ar/ayuda',
    last_verified: '2026-10-11',
  },
  {
    id: 'salud_responde',
    name: 'Salud Responde (Ministerio de Salud)',
    region: 'CL',
    channels: [
      { kind: 'phone', label: '600 360 7777', href: 'tel:6003607777', availableIn: 'CL' },
    ],
    hours:
      'Las 24 horas del día, los 365 días del año. La llamada telefónica tiene el valor del SLM, equivalente a una llamada local',
    description:
      'Salud Responde es una plataforma multicanal del Ministerio de Salud que entrega información, orientación y apoyo en temas de salud a toda la población.',
    url: 'https://saludresponde.minsal.cl/que-es-salud-responde/',
    last_verified: '2026-10-11',
  },
  {
    id: 'linea_4141',
    name: 'Línea de atención *4141 no estás solo, no estás sola (Ministerio de Salud)',
    region: 'CL',
    regionNote: 'Solo desde celulares',
    channels: [
      // No href: iOS will not dial a tel: link containing '*', so the star code is
      // shown unlinked, as SMS shortcodes are (Stuart, 2026-10-12).
      { kind: 'phone', label: '*4141 (desde celulares de cualquier compañía)', availableIn: 'CL' },
    ],
    hours: 'De lunes a domingo, las 24 horas del día. Gratuita',
    // The service's own self-description, which raises suicide or crisis directly.
    // Stuart chose to keep the entry with this wording on 2026-10-11.
    description:
      'La línea de prevención del suicidio es gratuita y a través de ella, las personas que estén enfrentando una emergencia o crisis de salud mental asociada al suicidio, podrán contactarse con un psicólogo que los escuchará y ayudará de lunes a domingo, las 24 horas del día.',
    url: 'https://www.minsal.cl/linea-de-atencion-4141-no-estas-solo-no-estas-sola/',
    last_verified: '2026-10-11',
  },
  {
    id: 'linea_106_minsalud',
    name: 'Línea 106 (Ministerio de Salud y Protección Social)',
    region: 'CO',
    channels: [
      { kind: 'phone', label: '106', href: 'tel:106', availableIn: 'CO' },
    ],
    hours:
      'Es gratuita, confidencial y funciona 24/7. Puede comunicarse desde teléfono fijo o celular',
    description:
      'La Línea 106 es un servicio de orientación en salud mental que brinda información, apoyo inicial y acompañamiento frente a situaciones que pueden afectar el bienestar emocional.',
    url: 'https://www.minsalud.gov.co/salud/publica/salud-mental/Paginas/linea-106.aspx',
    last_verified: '2026-10-11',
  },
  {
    id: 'telefono_de_la_esperanza',
    name: 'Teléfono de la Esperanza',
    region: 'ES',
    channels: [
      { kind: 'phone', label: '717 003 717', href: 'tel:717003717', availableIn: 'ES' },
      { kind: 'phone', label: '914 59 00 55', href: 'tel:914590055', availableIn: 'ES' },
    ],
    hours:
      'Servicio de Orientación telefónica 24h. El coste será el establecido por el operador del llamante (sin coste adicional dentro de tarifas planas). El sistema no acepta llamadas procedentes de números ocultos',
    description:
      'Este servicio es atendido por personas voluntarias formadas en escucha activa y preparadas para acompañar a la persona llamante en la búsqueda de sus propias capacidades para transitar el momento de crisis.',
    url: 'https://telefonodelaesperanza.org/intervencion-en-crisis',
    last_verified: '2026-10-11',
  },
  {
    id: 'linea_024',
    name: 'Línea 024 de atención a la conducta suicida',
    region: 'ES',
    channels: [
      { kind: 'phone', label: '024', href: 'tel:024', availableIn: 'ES' },
      {
        kind: 'webchat',
        label: 'Chat on-line',
        href: 'https://www2.cruzroja.es/web/cruzroja/chat-linea024',
        availableIn: 'ES',
      },
    ],
    hours: 'Disponible las 24 horas del día, los 365 días del año. Gratuito y confidencial',
    // The service's own self-description, which raises suicide or crisis directly.
    // Stuart chose to keep the entry with this wording on 2026-10-11.
    description:
      'Se trata de una línea telefónica de ayuda a las personas con pensamientos, ideaciones o riesgo de conducta suicida, y a sus familiares y allegados, básicamente a través de la contención emocional por medio de la escucha activa por los profesionales del 024, la recomendación de que contacten con los servicios sanitarios del SNS o la derivación al 112 en los casos en los que se aprecie una situación de emergencia.',
    url: 'https://www.sanidad.gob.es/linea024/home.htm',
    last_verified: '2026-10-11',
  },
  {
    id: 'linea_de_la_vida',
    name: 'Línea de la Vida',
    region: 'MX',
    channels: [
      { kind: 'phone', label: '800 911 2000', href: 'tel:8009112000', availableIn: 'MX' },
    ],
    hours:
      'Las 24 horas del día, los 365 días del año. Servicio gratuito, desde tu teléfono celular o fijo',
    description: 'Un profesional de la Salud Mental responde tu llamada, te escucha y orienta.',
    url: 'https://www.gob.mx/conasama/articulos/linea-de-la-vida-800-911-2000',
    last_verified: '2026-10-11',
  },
  {
    id: 'consejo_ciudadano_linea_de_seguridad',
    name: 'Línea de Seguridad y Chat de Confianza (Consejo Ciudadano)',
    region: 'MX',
    channels: [
      { kind: 'phone', label: '55 5533 5533', href: 'tel:5555335533', availableIn: 'MX' },
      {
        kind: 'webchat',
        label: 'Chat de Confianza (WhatsApp) 55 5533 5533',
        href: 'https://wa.me/525555335533',
        availableIn: 'MX',
      },
    ],
    hours:
      '24/7, los 365 días del año. Servicio gratuito y confidencial, desde cualquier parte del país',
    // The service's own self-description, which raises suicide or crisis directly.
    // Stuart chose to keep the entry with this wording on 2026-10-11.
    description:
      '¿Tienes ansiedad, depresión, estrés, estás pasando por un duelo o incluso has pensado en quitarte la vida?',
    url: 'https://consejociudadanomx.org/asi-te-ayudamos/contencion-emocional',
    last_verified: '2026-10-11',
  },
];

/**
 * Both of these describe themselves as bereavement services, and they are listed
 * with their own descriptions at no special prominence (Stuart, 2026-07-29): honest
 * about what they are, without the app being the thing that raises death with an
 * owner whose cat is alive. Do not add "when the time comes" framing, a euthanasia
 * section, or grief copy of our own around them.
 *
 * There is no Irish equivalent of either. ISPCA's 0818 line is for reporting cruelty
 * and DSPCA's pet-loss offering is a memorial garden — neither is support, so neither
 * belongs here.
 */
export const PET_SUPPORT_SERVICES: SupportService[] = [
  {
    id: 'paws_to_listen',
    name: 'Paws to Listen (Cats Protection)',
    region: 'UK',
    channels: [
      { kind: 'phone', label: '0800 024 94 94', href: 'tel:08000249494', availableIn: 'UK' },
    ],
    hours: '9am–5pm, Monday to Friday, excluding bank holidays. Free and confidential',
    description:
      'Grieving the loss or death of your pet? Our support line, Paws to Listen, helps owners at what can be a difficult time.',
    url: 'https://www.cats.org.uk/what-we-do/grief/paws-to-listen',
    last_verified: '2026-07-29',
  },
  {
    id: 'blue_cross_pet_loss',
    name: 'Pet Loss Support (Blue Cross)',
    region: 'UK_AND_IE',
    regionNote: 'UK by phone; webchat and email from anywhere',
    channels: [
      { kind: 'phone', label: '0800 096 6606', href: 'tel:08000966606', availableIn: 'UK' },
      {
        kind: 'webchat',
        label: 'Webchat',
        href: 'https://www.bluecross.org.uk/pet-loss-support',
        availableIn: 'UK_AND_IE',
      },
      {
        kind: 'email',
        label: 'Email',
        href: 'https://www.bluecross.org.uk/pet-loss-support',
        availableIn: 'UK_AND_IE',
      },
    ],
    hours:
      '8.30am–8.30pm every day by phone and webchat. Email replies within 48 hours',
    description:
      'If you are grieving for a pet, or facing loss, our free and confidential Pet Loss Support service is here for you by phone, email or webchat.',
    url: 'https://www.bluecross.org.uk/pet-loss-support',
    last_verified: '2026-07-29',
  },
];

/** Reference reading, not support services — outside the 90-day freshness test.
 *  Both confirmed to respond on 2026-07-29. */
export const REFERENCE_LINKS: ReferenceLink[] = [
  {
    id: 'tanyas_guide',
    name: "Tanya’s Comprehensive Guide to Feline CKD",
    region: 'UK_AND_IE',
    description: 'A long-running, owner-written reference on living with feline CKD.',
    url: 'https://www.felinecrf.org/',
    last_verified: '2026-07-29',
  },
  {
    id: 'iris',
    name: 'IRIS (International Renal Interest Society)',
    region: 'UK_AND_IE',
    description: 'The body that publishes the CKD staging guidelines vets work from.',
    url: 'https://www.iris-kidney.com/',
    last_verified: '2026-07-29',
  },
  {
    id: 'msd_vet_manual_urinary_ar',
    name: 'Manual de veterinaria de MSD — Propietarios de gatos',
    region: 'AR',
    description:
      'Página para propietarios sobre las enfermedades no infecciosas del aparato urinario de los gatos, con una sección sobre la enfermedad renal crónica.',
    url:
      'https://www.msdvetmanual.com/es/propietarios-de-gatos/trastornos-de-los-ri%C3%B1ones-y-del-tracto-urinario-de-los-gatos/enfermedades-no-infecciosas-del-aparato-urinario-de-los-gatos',
    last_verified: '2026-10-12',
  },
  {
    id: 'msd_vet_manual_urinary_cl',
    name: 'Manual de veterinaria de MSD — Propietarios de gatos',
    region: 'CL',
    description:
      'Página para propietarios sobre las enfermedades no infecciosas del aparato urinario de los gatos, con una sección sobre la enfermedad renal crónica.',
    url:
      'https://www.msdvetmanual.com/es/propietarios-de-gatos/trastornos-de-los-ri%C3%B1ones-y-del-tracto-urinario-de-los-gatos/enfermedades-no-infecciosas-del-aparato-urinario-de-los-gatos',
    last_verified: '2026-10-12',
  },
  {
    id: 'msd_vet_manual_urinary_co',
    name: 'Manual de veterinaria de MSD — Propietarios de gatos',
    region: 'CO',
    description:
      'Página para propietarios sobre las enfermedades no infecciosas del aparato urinario de los gatos, con una sección sobre la enfermedad renal crónica.',
    url:
      'https://www.msdvetmanual.com/es/propietarios-de-gatos/trastornos-de-los-ri%C3%B1ones-y-del-tracto-urinario-de-los-gatos/enfermedades-no-infecciosas-del-aparato-urinario-de-los-gatos',
    last_verified: '2026-10-12',
  },
  {
    id: 'msd_vet_manual_urinary_es',
    name: 'Manual de veterinaria de MSD — Propietarios de gatos',
    region: 'ES',
    description:
      'Página para propietarios sobre las enfermedades no infecciosas del aparato urinario de los gatos, con una sección sobre la enfermedad renal crónica.',
    url:
      'https://www.msdvetmanual.com/es/propietarios-de-gatos/trastornos-de-los-ri%C3%B1ones-y-del-tracto-urinario-de-los-gatos/enfermedades-no-infecciosas-del-aparato-urinario-de-los-gatos',
    last_verified: '2026-10-12',
  },
  {
    id: 'msd_vet_manual_urinary_mx',
    name: 'Manual de veterinaria de MSD — Propietarios de gatos',
    region: 'MX',
    description:
      'Página para propietarios sobre las enfermedades no infecciosas del aparato urinario de los gatos, con una sección sobre la enfermedad renal crónica.',
    url:
      'https://www.msdvetmanual.com/es/propietarios-de-gatos/trastornos-de-los-ri%C3%B1ones-y-del-tracto-urinario-de-los-gatos/enfermedades-no-infecciosas-del-aparato-urinario-de-los-gatos',
    last_verified: '2026-10-12',
  },
];

/** Every service on the page, in the order it is presented. */
export const ALL_SUPPORT_SERVICES: SupportService[] = [
  ...GENERAL_SUPPORT_SERVICES,
  ...PET_SUPPORT_SERVICES,
];

/** Days after which a service entry is considered stale and fails the build. */
export const SUPPORT_FRESHNESS_DAYS = 90;
